import type { FeatureKey } from '@/hooks/useFeatureToggles';
import type { ModuleKey } from '@/hooks/useTenantModules';
import {
  Home,
  FileText,
  Store,
  Euro,
  Gift,
  Megaphone,
  BarChart3,
  PiggyBank,
  UserCog,
  Globe,
  Settings,
  Boxes,
  GraduationCap,
  Hotel,
  UtensilsCrossed,
  Coffee,
  Wrench,
  ShoppingCart,
  ClipboardList,
  Tablet,
  Zap,
  Rocket,
  ShieldCheck,
  Banknote,
  Scale,
  Download,
  Activity,
  Bluetooth,
  Sparkles,
  FileCheck2,
} from 'lucide-react';

export interface SubItem {
  title: string;
  url: string;
  /** Visible uniquement pour rôle admin (owner tenant) ou super_admin. */
  adminOnly?: boolean;
  /** Visible uniquement si ce flag est actif pour le tenant (filtre AppSidebar). */
  featureKey?: FeatureKey;
}

export interface SubSection {
  title: string;
  children: SubItem[];
}

export interface MenuItem {
  title: string;
  url?: string;
  icon: React.ElementType;
  children?: SubItem[];
  sections?: SubSection[];
  /** Section verrouillée par PIN (Configuration par défaut). Long-press + code. */
  requiresPin?: boolean;
  /** Rubrique verrouillée par le code d'accès Certification (une fois par session). */
  codeLocked?: boolean;
  /** Visible uniquement pour super_admin hors impersonation (admin portal). */
  superAdminOnly?: boolean;
}

/** Module key associé à chaque sous-section de Configuration. Permet le filtrage
 *  granulaire par tenant même quand les modules sont imbriqués sous Configuration. */
export const SUBSECTION_MODULE_MAP: Record<string, ModuleKey> = {
  'Écran client': 'mod_ecran_client',
  Togo: 'mod_togo',
  Restaurant: 'mod_restaurant_salle',
  Hôtel: 'mod_hotel',
  Café: 'mod_bar',
};

export const menuItems: MenuItem[] = [
  { title: 'Accueil', url: '/dashboard', icon: Home },
  { title: 'Écran de caisse', url: '/caisse', icon: ShoppingCart },
  { title: 'Ticket W', url: '/bons-en-cours', icon: ClipboardList },
  { title: 'Tablette serveur', url: '/chr/serveur', icon: Tablet },
  { title: 'Caisse rapide', url: '/caisse-rapide', icon: Zap },
  // Monnayeur automatique : visible seulement si le flag tenant est actif (filtre AppSidebar).
  { title: 'Monnayeur', url: '/monnayeur', icon: Banknote },
  // Balance : visible seulement si le flag tenant est actif (filtre AppSidebar).
  { title: 'Balance', url: '/balance', icon: Scale },
  {
    title: 'Restaurant',
    icon: UtensilsCrossed,
    children: [
      { title: 'Plan de salle', url: '/chr/salle' },
      { title: 'Cuisine (KDS)', url: '/cuisine' },
      { title: 'Affichage salle', url: '/salle-display' },
      { title: 'Suivi commandes', url: '/commandes' },
    ],
  },
  {
    title: 'Fichiers',
    icon: FileText,
    children: [
      { title: 'Articles', url: '/produits' },
      { title: 'Étiquettes', url: '/etiquettes', featureKey: 'etiquettes_codes_barres' },
      { title: 'Intégrer', url: '/integrer', adminOnly: true },
      { title: 'Clients', url: '/clients' },
      { title: 'Fournisseurs', url: '/fournisseurs' },
    ],
  },
  {
    title: 'Magasin',
    icon: Store,
    children: [
      { title: 'Duplicata de ticket', url: '/duplicata' },
      { title: 'Note de frais', url: '/note-de-frais' },
      { title: 'Remboursement / Avoir', url: '/avoirs' },
      { title: 'Encours clients', url: '/encours' },
      { title: 'Bons de livraison', url: '/bons-de-livraison' },
      { title: 'Devis', url: '/devis', featureKey: 'devis_lekompte' },
      {
        title: 'Facturation des BL',
        url: '/facturation-bl',
        featureKey: 'facturation_lekompte',
        adminOnly: true,
      },
      {
        title: 'Factures clients',
        url: '/factures-clients',
        featureKey: 'facturation_lekompte',
        adminOnly: true,
      },
    ],
  },
  {
    title: 'Stocks',
    icon: Boxes,
    children: [
      { title: 'Mode Stock (scan)', url: '/mode-stock', featureKey: 'mode_stock' },
      { title: 'Etat des stocks', url: '/inventaire' },
      { title: 'Mouvements', url: '/mouvements' },
      { title: 'Inventaires', url: '/inventaires' },
    ],
  },
  {
    title: 'Fidélité',
    icon: Gift,
    children: [
      { title: "Bons de réduction et d'achat", url: '/bons-reduction' },
      { title: 'Type de fidélité', url: '/fidelite' },
      { title: 'Mouvements de fidélité', url: '/mouvements-fidelite' },
      { title: 'Cartes cadeaux', url: '/cartes-cadeaux' },
    ],
  },
  { title: 'Magic SMS', url: '/config/magic-sms', icon: Sparkles },
  {
    title: 'Marketing',
    icon: Megaphone,
    children: [
      { title: 'Promotions', url: '/promotions' },
      { title: 'Opérations commerciales', url: '/operations-commerciales' },
      { title: 'Personnalisation des documents', url: '/personnalisation-docs' },
    ],
  },
  {
    title: 'Rapports',
    icon: BarChart3,
    children: [
      { title: 'KPI avancés', url: '/dashboard/kpi' },
      { title: 'Rapport de ventes', url: '/ventes' },
      { title: 'Palmarès articles', url: '/rapports' },
    ],
  },
  {
    title: 'Finance',
    icon: PiggyBank,
    children: [
      { title: 'Clôture & envoi comptable', url: '/finance/cloture-comptable' },
      { title: 'Coffre', url: '/coffre' },
      { title: 'Informations comptables', url: '/comptabilite' },
      { title: 'Transfert comptable', url: '/transfert-comptable' },
      { title: 'Lekompte', url: '/finance/lekompte' },
    ],
  },
  // Gestion fiscal — ex "Gestion", placée juste après Finance (vues admin
  // documents fiscaux/sessions/clôtures/journal). Plus dans la zone verrouillée.
  {
    title: 'Gestion fiscal',
    icon: Euro,
    children: [
      { title: 'Liste des pièces commerciales', url: '/pieces-commerciales' },
      { title: 'Détails des lignes de vente', url: '/lignes-vente' },
      { title: 'Sessions de caisse', url: '/sessions-caisse' },
      { title: 'Clôtures Z (auto-cert)', url: '/clotures-z' },
      { title: 'Journal fiscal (JET)', url: '/journal-fiscal' },
      { title: 'Transactions CB', url: '/transactions-cb' },
      { title: 'Transactions CB externes', url: '/transactions-cb-externes' },
    ],
  },
  {
    title: 'Equipe',
    icon: UserCog,
    children: [
      { title: 'Utilisateurs', url: '/utilisateurs' },
      { title: "Profils d'accès", url: '/profils-acces' },
    ],
  },
  {
    title: 'Website',
    icon: Globe,
    children: [
      { title: 'Commandes', url: '/commandes-web' },
      { title: 'Paramètres', url: '/parametres-web' },
    ],
  },
  {
    title: 'Configuration',
    icon: Settings,
    requiresPin: true,
    sections: [
      {
        title: 'Paramètres',
        children: [
          { title: 'Gestion des modules', url: '/config/modules' },
          { title: 'Feature flags', url: '/config/feature-flags' },
          { title: 'Mode kiosque / Device', url: '/config/kiosk' },
          { title: 'Appareils (PDA stock)', url: '/config/appareils', adminOnly: true },
          { title: 'Univers', url: '/config/univers' },
          { title: 'Profils fidélité', url: '/config/profils-fidelite' },
          { title: 'Cartes à tampons', url: '/config/fidelite-punch' },
          { title: 'Identités', url: '/config/identites' },
          { title: 'Codes postaux', url: '/config/codes-postaux' },
          { title: 'Pays', url: '/config/pays' },
          { title: 'Zones de livraison', url: '/config/zones-livraison' },
          { title: 'Imprimantes', url: '/imprimantes' },
          { title: 'Monnayeur', url: '/monnayeur' },
          { title: 'Postes de travail', url: '/config/postes-travail' },
          { title: 'Import de données', url: '/config/import' },
          { title: 'Paramètres du dossier', url: '/config/parametres-dossier' },
        ],
      },
      {
        title: 'Écran client',
        children: [{ title: 'Playlist & écrans', url: '/config/ecran-client' }],
      },
      {
        title: 'Togo',
        children: [
          { title: 'Matériel configuré', url: '/togo/materiel' },
          { title: 'Imprimantes', url: '/togo/imprimantes' },
          { title: 'Tickets de caisse', url: '/togo/tickets' },
          { title: 'Terminaux de paiement', url: '/togo/tpe' },
        ],
      },
      {
        title: 'Restaurant',
        children: [
          { title: 'Plan de salle', url: '/chr/salle' },
          { title: 'Cuisine (KDS)', url: '/cuisine' },
          { title: 'Affichage salle', url: '/salle-display' },
          { title: 'Suivi commandes', url: '/commandes' },
        ],
      },
      {
        title: 'Hôtel',
        children: [
          { title: 'Chambres', url: '/chr/chambres' },
          { title: 'Registre voyageurs', url: '/chr/voyageurs' },
        ],
      },
      {
        title: 'Café',
        children: [{ title: 'Ardoises / Tabs', url: '/chr/bar' }],
      },
      {
        title: 'Classifications articles',
        children: [
          { title: 'Familles', url: '/config/familles' },
          { title: 'Sous-familles', url: '/config/sous-familles' },
          { title: 'Marques', url: '/config/marques' },
          { title: 'Classifications 1', url: '/config/classifications-1' },
          { title: 'Classifications 2', url: '/config/classifications-2' },
          { title: 'Classifications 3', url: '/config/classifications-3' },
          { title: 'Caractéristiques', url: '/config/caracteristiques' },
          { title: 'Variantes', url: '/config/variantes' },
        ],
      },
      {
        title: 'Qualification',
        children: [
          { title: 'Familles clients', url: '/config/familles-clients' },
          { title: 'Familles fournisseurs', url: '/config/familles-fournisseurs' },
          { title: 'Familles tarifaires', url: '/config/familles-tarifaires' },
          { title: 'Provenances clients', url: '/config/provenances' },
          { title: 'Lignes de commentaire', url: '/config/commentaires' },
          { title: 'Libellés', url: '/config/libelles' },
          { title: 'Type de mouvement de stock', url: '/config/types-mouvement' },
        ],
      },
      {
        title: 'Financier',
        children: [
          { title: 'Modes de règlement', url: '/config/modes-reglement' },
          { title: 'Paiements myPOS', url: '/paiements-mypos' },
          { title: 'TPE — Credentials fournisseur', url: '/config/tpe' },
          { title: 'Délais de paiement', url: '/config/delais-paiement' },
          { title: 'Fonds de caisse', url: '/config/fonds-caisse' },
          { title: 'Profils comptables', url: '/config/profils-comptables' },
          { title: 'Profils TVA', url: '/config/profils-tva' },
          { title: 'Taxes', url: '/config/taxes' },
          { title: 'Comptes bancaires', url: '/config/comptes-bancaires' },
          { title: 'Devises', url: '/config/devises' },
        ],
      },
    ],
  },
  { title: 'Démarrage', url: '/config/modules', icon: Rocket },
  { title: 'SPEED BT', url: '/config/tpe-bluetooth', icon: Bluetooth },
  { title: 'Tutoriels', url: '/tutoriels', icon: GraduationCap },
  // Dernière ligne du menu commerçant, verrouillée par code (src/lib/certification-lock.ts)
  {
    title: 'Certification',
    icon: FileCheck2,
    codeLocked: true,
    children: [
      { title: 'Certificat', url: '/config/certificat' },
      { title: "Pistes d'audit", url: '/config/pistes-audit' },
      { title: 'Périodes journalières', url: '/config/periodes-jour' },
      { title: 'Périodes mensuelles', url: '/config/periodes-mois' },
      { title: 'Périodes annuelles', url: '/config/periodes-annee' },
      { title: 'Périodes comptables', url: '/config/periodes-comptable' },
      { title: 'Archives fiscales (mensuelles)', url: '/config/archives-fiscales' },
    ],
  },
  // Admin LinkVision — filtré dans AppSidebar (isSuperAdmin && !isImpersonating)
  {
    title: 'Monitoring tenants',
    url: '/admin-portal/monitoring',
    icon: Activity,
    superAdminOnly: true,
  },
  {
    title: 'Installation',
    url: '/admin-portal/installation',
    icon: Download,
    superAdminOnly: true,
  },
  {
    title: 'Monnayeurs (parc)',
    url: '/admin-portal/monnayeurs',
    icon: Banknote,
    superAdminOnly: true,
  },
  { title: 'Admin Portal', url: '/admin-portal', icon: ShieldCheck, superAdminOnly: true },
];

export { DEMO_SECTORS, DEMO_CUSTOMERS } from './demoData';
